import { IProduceBackupManager, IProduceBackupManagerDelegates } from './produce-backup-manager.interface';
import {
    IProduceBackupInjectable,
} from '@src/slide/application/service/produce-backup-manager/inject/produce-backup-injectable.interface';
import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import { getUrlParameter } from '@src/slide/infrastructure/module/independent/url/param-operation';
import i18n from '@i18next/i18n';
import SingleSnackbar from '@utils/single-snackbar';
import { backupShowDialog, IAidOptions } from '@src/js/common/ark-extensions/dc-components/docs-component-size-limit-dialog';
import { monitor } from '@tencent/tencent-doc-report';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { SlideDataServices, onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import Modal from '@tencent/dui/lib/components/Modal';
import { PAY_EXTENSION } from '@src/js/common/ark-extensions/dc-components/pay';
import ArkLoader from '@src/js/common/ark-extensions';
import ua from '@tencent/docs-user-agent';

enum EVipStatus {
    NO_STATUS = 0, // 错误或者该类型未开启使用状态
    CLOSE = 1, // 关闭状态，主要检测用户是否开通过该类型会员，一旦开通则不会在处于该状态
    OPEN = 2, //  正常使用状态
    EXPIRED = 3, // 过期状态
}

export class ProduceBackupManager implements IProduceBackupManager {
    protected services: IProduceBackupManagerDelegates;
    protected singleSnackbar: SingleSnackbar;
    protected readonly shouldReport: boolean = true;
    protected injectable: IProduceBackupInjectable;

    public constructor() {
        this.singleSnackbar = new SingleSnackbar({ Snackbar: DUISnackbar });
    }

    public setServiceDelegates(services: IProduceBackupManagerDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public setInject(injectable: IProduceBackupInjectable): void {
        this.injectable = injectable;
    }

    public async generateProduceBackup(params: { aidOptions?: IAidOptions } = {}) {
        const { aidOptions } = params;

        this.injectable.produceBackup(aidOptions);
    }

    // 异常场景无需再在新开的窗口显示信息，只要在当前页面toast即可
    public onCatchCreateDocNew = () => {
        this.singleSnackbar.show({
            message: i18n.t('生成副本失败，请稍后重试'),
            type: 'error',
            autoClose: true,
        });
        this.shouldReport && monitor(33586553);
        this.shouldReport && window.speedReport?.markEnd(window.speedReport.reportId.docDuplicate);
    };

    // 创建副本失败的默认toast信息
    public showCreateDocNewErrorToast = (res, licenseErrMsg?, commonErrMsg?) => {
        // eslint-disable-next-line no-undef
        const toastInfo = this.injectable.getCreateDocNewErrorToastInfo(res, licenseErrMsg, commonErrMsg);
        this.singleSnackbar.show({
            message: toastInfo.msg,
            // @ts-ignore
            type: toastInfo.type,
            autoClose: true,
        });
    };

    public redirectToDoc = (url) => {
    };

    public produceBackUpCheck = () => {
        onSlideAppReady(() => {
            this.checkBack();
        });
    };

    protected checkBack = async () => {
        const { newPad, newPadType } = SlideDataServices.ClientVarsService()?.getClientVars() || {};
        if ((getUrlParameter('newPad') !== null || newPad)) {
            if ((getUrlParameter('newPadType') === 'clone' || newPadType === 'clone')) {
                this.handleCheckBack();
            }
        }
    };

    protected handleCheckBack = () => {
    };

    // eslint-disable-next-line complexity
    protected onCreateDocNew = async (response, aidOptions?: IAidOptions) => {
        const res = response.data;
        if (res.retcode !== 0) {
            // 判断属于存储空间超限阻断场景的话，显示开通会员扩容提示, 不再往后走
            const hasShowDialog = await backupShowDialog(res.retcode, aidOptions);
            if (!hasShowDialog) {
                // 其他场景直接toast提示
                this.showCreateDocNewErrorToast(res);
            } else {
                this.singleSnackbar.close();
            }
            this.shouldReport && monitor(33586553);
            return false;
        }

        this.handleCreateDocNew(res);
        return true;
    };

    protected handleCreateDocNew = (res) => {
    };

    protected checkShowProducingSnackbar() {
        return true;
    }

    protected getTitle = () => {
        const { title = '' } = SlideDataServices.SlideTitleManager() ?? {};
        return title.length > 125
            ? title.substr(0, 125) : title;
    };

    private isMyselfVip(): boolean {
        const vipInfo = (window as any).clientVars?.vipInfo;
        if (vipInfo.isVip) {
            return true;
        }
        if (vipInfo.vip?.status === EVipStatus.OPEN) {
            return true;
        }
        if (vipInfo.vvip?.status === EVipStatus.OPEN) {
            return true;
        }
        if (vipInfo.svip?.status === EVipStatus.OPEN) {
            return true;
        }
        return false;
    }

    private checkAndHandleNonVipCopyWithVipFonts = async () => {
        const fontUsingStatus = await this.services?.fontManager?.collectFontsUsingStatus();
        if (fontUsingStatus?.hasVipFonts && !this.isMyselfVip()) {
            Modal.confirm({
                title: i18n.t('无法生成副本'),
                content: i18n.t('开通会员，即可生成包含个性字体的文件副本'),
                okButtonType: 'golden',
                okText: i18n.t('开通会员'),
                onOk: () => {
                    ArkLoader.extensionService.getExtension(PAY_EXTENSION.name).then((pay) => {
                        pay.show({
                            source: this.getPreventCloneVipAid(),
                        });
                    });
                },
            });
            return false;
        }
        return true;
    };

    private getPreventCloneVipAid() {
        return `txdocs_${this.getAidPlatformShorthand()}_${this.getAidSystemShorthand()}_titlebar_typeface_copy_slide`;
    }

    private getAidSystemShorthand() {
        let system = 'web';
        if (ua.isWxMiniProgram) {
            system = 'wxsp';
        } else if (ua.isTencentDocsApp) {
            system = 'app';
        } else if (ua.isPCTencentDocsClient) {
            system = 'desk';
        } else if (ua.isMobile) {
            system = 'h5';
        }
        return system;
    }

    private getAidPlatformShorthand() {
        let platform = '';
        if (ua.isAndroid) {
            platform = 'an';
        } else if (ua.isIOS) {
            platform = 'ios';
        } else if (ua.isWindows) {
            platform = 'wd';
        } else if (ua.isMac) {
            platform = 'mac';
        }
        return platform;
    }
}
